import { useTranslation } from "react-i18next";
import { dayjs } from "Config/dayjs";
import { useForm } from "antd/lib/form/Form";
import { I18N_NS } from "Config/i18n";
import { DatePicker, Drawer, Form, Input, Space, Button, Select, Divider } from "antd";
import { ADMIN_getFilteredAdminLogs } from "General/APIQueryParams";

interface FilterDrawerProps {
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (values: ADMIN_getFilteredAdminLogs) => Promise<void>;
  onClose: () => void;
  onResetFilter: () => void;
  logTypes: string[];
  initialValues?: ADMIN_getFilteredAdminLogs;
}

interface FormValues extends Omit<ADMIN_getFilteredAdminLogs, "startTime" | "endTime"> {
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

export const FilterDrawer = (props: FilterDrawerProps) => {
  const { isOpen, onClose, onSubmit, logTypes, isLoading, onResetFilter, initialValues } = props;
  const { t } = useTranslation(I18N_NS.ADMIN);
  const [form] = useForm<FormValues>();

  const getFormInitialValues = (): FormValues => {
    if (!initialValues) return {};
    const { startTime, endTime, ...rest } = initialValues;
    return {
      ...rest,
      dateRange: startTime && endTime ? [dayjs(startTime), dayjs(endTime)] : undefined,
    };
  };

  const handleFinish = async ({ dateRange, ...values }: FormValues) => {
    try {
      const formData: ADMIN_getFilteredAdminLogs = { ...values };
      if (dateRange) {
        formData.startTime = +dateRange[0].format("x");
        formData.endTime = +dateRange[1].format("x");
      }
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      throw error;
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleResetFields = () => {
    onResetFilter();
    form.setFieldsValue({
      dateRange: undefined,
      actorId: undefined,
      actorName: undefined,
      ip: undefined,
      logType: undefined,
      targetType: undefined,
      targetName: undefined,
      targetID: undefined,
    });
  };

  return (
    <Drawer
      title={t("Filter logs")}
      placement={"right"}
      onClose={handleClose}
      open={isOpen}
      size={450}
      extra={
        <Space>
          <Button onClick={handleResetFields}>{t("Reset")}</Button>
          <Button loading={isLoading} onClick={() => form.submit()} type="primary">
            {t("Submit")}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={getFormInitialValues()}
        onKeyUp={(e) => {
          if (e.key === "Enter") {
            form.submit();
          }
        }}
      >
        <Form.Item name="dateRange">
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            showTime
            format={"L HH:mm"}
            presets={[
              { label: t("Last 48 hours"), value: [dayjs().subtract(48, "hours"), dayjs()] },
              { label: t("Today"), value: [dayjs().startOf("day"), dayjs()] },
              { label: t("Last hour"), value: [dayjs().subtract(1, "hour"), dayjs()] },
              { label: t("15 minutes"), value: [dayjs().subtract(15, "minutes"), dayjs()] },
              { label: t("From yesterday"), value: [dayjs().subtract(1, "day").startOf("day"), dayjs()] },
            ]}
          />
        </Form.Item>
        <Form.Item name="actorId">
          <Input placeholder={t("Actor ID")} allowClear />
        </Form.Item>
        <Form.Item name="actorName">
          <Input placeholder={t("Actor name")} allowClear />
        </Form.Item>
        <Form.Item name="ip">
          <Input placeholder={t("IP (Exact matches only)")} allowClear />
        </Form.Item>
        <Form.Item name="logType">
          <Select
            placeholder={t("Log-type")}
            options={logTypes.map((i) => ({ value: i, text: i }))}
            showSearch={{
              filterOption: true,
            }}
            allowClear
          />
        </Form.Item>
        <Divider>{t("Target")}</Divider>
        <Form.Item name="targetType">
          <Select<ADMIN_getFilteredAdminLogs["targetType"]>
            placeholder={t("Type")}
            options={[
              { label: "USER", value: "USER" },
              { label: "PROJECT", value: "PROJECT" },
              { label: "PROJECTGROUP", value: "PROJECTGROUP" },
            ]}
            allowClear
          />
        </Form.Item>
        <Form.Item name="targetName">
          <Input placeholder={t("Shortname")} allowClear />
        </Form.Item>
        <Form.Item name="targetID" rules={[{ type: "integer", transform: (v) => (v ? parseInt(v) : undefined) }]}>
          <Input placeholder={t("ID (Numeric)")} allowClear />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
